import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Sun, Moon, Plus, Check, Trash2, Circle, CheckCircle2, Sparkles, RotateCcw, Edit2, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_MORNING_TASKS = [
  'Vakna utan snooze',
  'Drick ett glas vatten',
  'Stretcha 5 minuter',
  'Meditera/andas',
  'Ät en nyttig frukost',
  'Planera dagens viktigaste uppgifter',
  'Klä dig som du vill känna dig',
];

const DEFAULT_EVENING_TASKS = [
  'Stäng av skärmar 1h före läggdags',
  'Förbered morgondagens kläder',
  'Skriv 3 saker du är tacksam för',
  'Läs i 15 minuter',
  'Skönhetrutin/hudvård',
  'Reflektera över dagen',
  'Lägg dig vid samma tid',
];

export default function RoutinesPage() {
  const navigate = useNavigate();
  const { hasAccess, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('morning');
  const [routines, setRoutines] = useState({ morning: [], evening: [] });
  const [todayProgress, setTodayProgress] = useState({ morning: [], evening: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newTask, setNewTask] = useState('');

  useEffect(() => {
    if (hasAccess) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const loadData = async () => {
    try {
      const response = await axios.get(`${API}/routines`, {
        headers: getAuthHeader()
      });
      const data = response.data;
      
      // Format routines from backend
      const morningTasks = (data.morning || []).map((item, i) => ({
        id: item.id || i + 1,
        text: item.text || item
      }));
      const eveningTasks = (data.evening || []).map((item, i) => ({
        id: item.id || i + 100,
        text: item.text || item
      }));
      
      setRoutines({
        morning: morningTasks.length > 0 ? morningTasks : DEFAULT_MORNING_TASKS.map((text, i) => ({ id: i + 1, text })),
        evening: eveningTasks.length > 0 ? eveningTasks : DEFAULT_EVENING_TASKS.map((text, i) => ({ id: i + 100, text }))
      });
      
      // Load today's progress from backend data
      const today = new Date().toISOString().split('T')[0];
      const lastReset = data.lastReset ? data.lastReset.split('T')[0] : null;
      
      if (lastReset === today) {
        // Progress was reset today, load completed items
        const morningCompleted = (data.morning || []).filter(t => t.completed).map(t => t.id);
        const eveningCompleted = (data.evening || []).filter(t => t.completed).map(t => t.id);
        setTodayProgress({ morning: morningCompleted, evening: eveningCompleted });
      } else {
        // Check localStorage for today's progress as backup
        const savedProgress = localStorage.getItem(`routine-progress-${today}`);
        if (savedProgress) {
          setTodayProgress(JSON.parse(savedProgress));
        } else {
          setTodayProgress({ morning: [], evening: [] });
        }
      }
    } catch (error) {
      console.error('Failed to load routines:', error);
      // Fallback to localStorage
      const savedRoutines = localStorage.getItem('daily-routines');
      if (savedRoutines) {
        setRoutines(JSON.parse(savedRoutines));
      } else {
        const defaults = {
          morning: DEFAULT_MORNING_TASKS.map((text, i) => ({ id: i + 1, text })),
          evening: DEFAULT_EVENING_TASKS.map((text, i) => ({ id: i + 100, text }))
        };
        setRoutines(defaults);
      }
      
      const today = new Date().toISOString().split('T')[0];
      const savedProgress = localStorage.getItem(`routine-progress-${today}`);
      if (savedProgress) {
        setTodayProgress(JSON.parse(savedProgress));
      }
    } finally {
      setLoading(false);
    }
  };

  const saveRoutines = async (updated) => {
    setRoutines(updated);
    setSaving(true);
    
    try {
      // Format for backend with completed status
      const today = new Date().toISOString().split('T')[0];
      const backendData = {
        morning: updated.morning.map(t => ({
          id: t.id,
          text: t.text,
          completed: todayProgress.morning?.includes(t.id) || false
        })),
        evening: updated.evening.map(t => ({
          id: t.id,
          text: t.text,
          completed: todayProgress.evening?.includes(t.id) || false
        })),
        lastReset: today
      };
      
      await axios.put(`${API}/routines`, backendData, {
        headers: getAuthHeader()
      });
      localStorage.setItem('daily-routines', JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save routines:', error);
      localStorage.setItem('daily-routines', JSON.stringify(updated));
    } finally {
      setSaving(false);
    }
  };

  const saveProgress = async (updated) => {
    const today = new Date().toISOString().split('T')[0];
    setTodayProgress(updated);
    localStorage.setItem(`routine-progress-${today}`, JSON.stringify(updated));
    
    // Also update backend with progress
    try {
      const backendData = {
        morning: routines.morning.map(t => ({
          id: t.id,
          text: t.text,
          completed: updated.morning?.includes(t.id) || false
        })),
        evening: routines.evening.map(t => ({
          id: t.id,
          text: t.text,
          completed: updated.evening?.includes(t.id) || false
        })),
        lastReset: today
      };
      
      await axios.put(`${API}/routines`, backendData, {
        headers: getAuthHeader()
      });
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  const toggleTask = (taskId) => {
    const current = todayProgress[activeTab] || [];
    let updated;
    if (current.includes(taskId)) {
      updated = current.filter(id => id !== taskId);
    } else {
      updated = [...current, taskId];
    }
    saveProgress({ ...todayProgress, [activeTab]: updated });
    
    // Check if all tasks completed
    const allTasks = routines[activeTab].length;
    if (updated.length === allTasks && allTasks > 0) {
      toast.success(`🎉 ${activeTab === 'morning' ? 'Morgonrutin' : 'Kvällsrutin'} klar!`);
    }
  };

  const addTask = () => {
    if (!newTask.trim()) return;
    const task = { id: Date.now(), text: newTask.trim() };
    const updated = {
      ...routines,
      [activeTab]: [...routines[activeTab], task]
    };
    saveRoutines(updated);
    setNewTask('');
    toast.success('Uppgift tillagd!');
  };

  const removeTask = (taskId) => {
    const updated = {
      ...routines,
      [activeTab]: routines[activeTab].filter(t => t.id !== taskId)
    };
    saveRoutines(updated);
    // Also remove from progress
    const updatedProgress = {
      ...todayProgress,
      [activeTab]: (todayProgress[activeTab] || []).filter(id => id !== taskId)
    };
    saveProgress(updatedProgress);
    toast.success('Uppgift borttagen');
  };

  const resetToDefaults = async () => {
    const defaults = {
      morning: DEFAULT_MORNING_TASKS.map((text, i) => ({ id: Date.now() + i, text })),
      evening: DEFAULT_EVENING_TASKS.map((text, i) => ({ id: Date.now() + i + 100, text }))
    };
    await saveRoutines(defaults);
    await saveProgress({ morning: [], evening: [] });
    toast.success('Återställt till standardrutiner');
  };

  const getProgress = (type) => {
    const tasks = routines[type]?.length || 0;
    const done = todayProgress[type]?.length || 0;
    if (tasks === 0) return 0;
    return Math.round((done / tasks) * 100);
  };

  const morningProgress = getProgress('morning');
  const eveningProgress = getProgress('evening');

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="routines-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/home')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Rutiner</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till rutiner.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="routines-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/home')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Morgon & Kvällsrutin</h1>
          </div>
          <Button variant="ghost" onClick={() => setEditMode(!editMode)} className="rounded-full">
            {editMode ? <X className="w-5 h-5" /> : <Edit2 className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-6 space-y-6">
        {/* Progress summary */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => setActiveTab('morning')}
            className={`p-4 rounded-2xl border transition-all ${
              activeTab === 'morning' 
                ? 'bg-amber-50 border-amber-200' 
                : 'bg-white border-border/30 hover:border-amber-200'
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                activeTab === 'morning' ? 'bg-amber-200 text-amber-700' : 'bg-amber-100 text-amber-600'
              }`}>
                <Sun className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold">Morgonrutin</h3>
                <p className="text-xs text-muted-foreground">{routines.morning?.length || 0} uppgifter</p>
              </div>
            </div>
            <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-500 transition-all rounded-full"
                style={{ width: `${morningProgress}%` }}
              />
            </div>
            <p className={`text-right text-sm mt-1 ${morningProgress === 100 ? 'text-amber-600 font-semibold' : 'text-muted-foreground'}`}>
              {morningProgress === 100 ? '✓ Klar!' : `${morningProgress}%`}
            </p>
          </button>

          <button
            onClick={() => setActiveTab('evening')}
            className={`p-4 rounded-2xl border transition-all ${
              activeTab === 'evening' 
                ? 'bg-indigo-50 border-indigo-200' 
                : 'bg-white border-border/30 hover:border-indigo-200'
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                activeTab === 'evening' ? 'bg-indigo-200 text-indigo-700' : 'bg-indigo-100 text-indigo-600'
              }`}>
                <Moon className="w-5 h-5" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold">Kvällsrutin</h3>
                <p className="text-xs text-muted-foreground">{routines.evening?.length || 0} uppgifter</p>
              </div>
            </div>
            <div className="h-2 bg-indigo-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-indigo-500 transition-all rounded-full"
                style={{ width: `${eveningProgress}%` }}
              />
            </div>
            <p className={`text-right text-sm mt-1 ${eveningProgress === 100 ? 'text-indigo-600 font-semibold' : 'text-muted-foreground'}`}>
              {eveningProgress === 100 ? '✓ Klar!' : `${eveningProgress}%`}
            </p>
          </button>
        </div>

        {/* Active routine tasks */}
        <div className={`rounded-2xl p-6 ${activeTab === 'morning' ? 'bg-amber-50' : 'bg-indigo-50'}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
              {activeTab === 'morning' ? <Sun className="w-5 h-5 text-amber-600" /> : <Moon className="w-5 h-5 text-indigo-600" />}
              {activeTab === 'morning' ? 'Morgonrutin' : 'Kvällsrutin'}
            </h2>
            {editMode && (
              <button 
                onClick={resetToDefaults}
                className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <RotateCcw className="w-4 h-4" />
                Återställ
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {routines[activeTab]?.map((task) => {
                const isDone = (todayProgress[activeTab] || []).includes(task.id);
                return (
                  <div
                    key={task.id}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isDone 
                        ? 'bg-white/50' 
                        : 'bg-white shadow-sm'
                    }`}
                  >
                    <button
                      onClick={() => !editMode && toggleTask(task.id)}
                      disabled={editMode}
                      className={`w-6 h-6 flex items-center justify-center transition-all ${
                        editMode ? 'opacity-50' : ''
                      }`}
                    >
                      {isDone ? (
                        <CheckCircle2 className={`w-6 h-6 ${activeTab === 'morning' ? 'text-amber-500' : 'text-indigo-500'}`} />
                      ) : (
                        <Circle className="w-6 h-6 text-gray-300" />
                      )}
                    </button>
                    <span className={`flex-1 ${isDone ? 'line-through text-muted-foreground' : ''}`}>
                      {task.text}
                    </span>
                    {editMode && (
                      <button
                        onClick={() => removeTask(task.id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}

              {routines[activeTab]?.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  Inga uppgifter än. Lägg till din första!
                </p>
              )}

              {/* Add new task */}
              {editMode && (
                <div className="flex gap-2 mt-4">
                  <Input
                    value={newTask}
                    onChange={(e) => setNewTask(e.target.value)}
                    placeholder="Lägg till ny uppgift..."
                    onKeyPress={(e) => e.key === 'Enter' && addTask()}
                    className="bg-white"
                  />
                  <Button onClick={addTask} className="rounded-xl px-4" data-testid="add-routine-task-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Lägg till
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tips */}
        <div className={`rounded-2xl p-4 ${activeTab === 'morning' ? 'bg-amber-100/50 border border-amber-200' : 'bg-indigo-100/50 border border-indigo-200'}`}>
          <div className="flex items-start gap-3">
            <Sparkles className={`w-5 h-5 mt-0.5 ${activeTab === 'morning' ? 'text-amber-600' : 'text-indigo-600'}`} />
            <div>
              <h4 className={`font-semibold text-sm ${activeTab === 'morning' ? 'text-amber-800' : 'text-indigo-800'}`}>
                {activeTab === 'morning' ? 'Tips för morgonen' : 'Tips för kvällen'}
              </h4>
              <p className={`text-sm ${activeTab === 'morning' ? 'text-amber-700' : 'text-indigo-700'}`}>
                {activeTab === 'morning' 
                  ? 'Gör det enkelt! Lägg fram träningskläder kvällen innan och ställ ett glas vatten vid sängen.'
                  : 'Samma läggdags varje kväll hjälper din kropp att reglera sömnen. Undvik skärmar minst 30 min innan.'}
              </p>
            </div>
          </div>
        </div>

        {/* Edit mode instructions */}
        {!editMode && (
          <p className="text-xs text-muted-foreground text-center">
            Tryck på pennan uppe till höger för att anpassa dina rutiner
          </p>
        )}
      </main>
    </div>
  );
}
